import { DOCUMENT, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, throwError } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { CONSUMER_FORM } from '../../features/user/user-form-type';

export enum USER_ROLE {
  CONSUMER = 'consumer',
  ADVISOR = 'advisor',
  SUPPLIER = 'supplier',
  SPONSOR = 'sponsor',
}
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private httpClient = inject(HttpClient);
  document = inject(DOCUMENT);
  platformId = inject(PLATFORM_ID);
  
  // Use a getter for currentUser to safe guard SSR access if needed, or initialize carefully.
  // signal(this.getUser()) calls getUser() immediately, so getUser must be safe.
  currentUser = signal<any>(this.getUser());

  private redirect_url_password = `${this.document.location.origin}/auth/login/`;
  private redirect_url_register = `${this.document.location.origin}/auth/email-verification/`;
  private redirect_url_resend_verification_email = `${this.document.location.origin}/auth/email-verification/`;

  async login(email: string, password: string) {
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/auth/login`, {
        email,
        password,
      })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async register(data: any) {
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/auth/register`, {
        ...data,
        redirect_url: this.redirect_url_register + data.email
      })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async forgotPassword(email: string) {
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/auth/forgot-password`, { email, redirect_url: this.redirect_url_password + email + '/reset' })
        .pipe(
          map((response: any) => {
            return response;
          })
        ));
  }

  async verifyEmail(code: string) {
    return firstValueFrom(
      this.httpClient.patch(`${this.apiUrl}/auth/email-verification`, { code }, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response;
          })
        ));
  }

  async resendVerificationEmail(email: string) {
    return firstValueFrom(
      this.httpClient.patch(`${this.apiUrl}/auth/re-sent-email-verification`, { redirect_url: this.redirect_url_resend_verification_email + email }, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response;
          })
        ));
  }

  async getUserData() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/auth/profile`, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  parseErrors(error: any) {
    if (error.errors) {
      return Object.keys(error.errors).map((key: any) => {
        return error.errors[key][0];
      });
    }

    return error.message || 'Ha ocurrido un error, inténtelo de nuevo';
  }

  saveToken(data: any) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('auth_token', data.token || data.access_token);
      localStorage.setItem('auth_refresh_token', data['refresh-token'] || data.refresh_token);
      localStorage.setItem('auth_token_type', data.type || data.token_type);
    }
  }

  getToken() {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('auth_token');
    }
    return null;
  }

  getTokenType() {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('auth_token_type');
    }
    return null;
  }

  getRefreshToken() {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('auth_refresh_token');
    }
    return null;
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `${this.getTokenType()} ${this.getToken()}`
    };
  }

  saveUserData(data: any) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('user_data', JSON.stringify(data));
    }
    this.currentUser.set(data);
  }

  getUser() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const user = JSON.parse(localStorage.getItem('user_data') || '{}');
        return Object.keys(user).length > 0 ? user : null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  async createNewsLettersApi(email: string) {
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/news-letters`, { email }, {
        headers: {
          'Authorization': `${this.getTokenType()} ${this.getToken()}`,
          'Content-Type': 'application/json',
        },
      })
    );
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_token_type');
      localStorage.removeItem('user_data');
    }
    this.currentUser.set(null);
  }

  // handle token refresh
  getNewToken() {
    const tokenType = this.getTokenType();
    const refreshToken = this.getRefreshToken();

    if (!tokenType || !refreshToken) {
      console.warn('Cannot refresh token: Missing token type or refresh token');
      return Promise.reject(new Error('Missing tokens'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `${tokenType} ${refreshToken}`
    };
    
    return firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/auth/refresh-token`, {}, { headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
    ));
  }

  // user functions
  async updateProfile(data: any, endpoint: string | null = 'profile/user-update') {
    const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/client');
    
    return firstValueFrom(
      this.httpClient.put(`${apiUrl}/${endpoint}`, data, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  // reset password
  async resetPassword(data: any) {
    const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/client');
    return firstValueFrom(
      this.httpClient.patch(`${apiUrl}/profile/reset-password`, {
        ...data,
        redirect_url: this.redirect_url_password,
      }, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async getGenders() {
    // const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/admin');
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/datas/genders`, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async getCities() {
    const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/admin');
    return firstValueFrom(
      this.httpClient.get(`${apiUrl}/datas/cities`, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async getBusinessRoles() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/datas/business-rol`)
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
  }

  async getCatalogs(catalogs: string[]) {

    const role = this.getUserRole();

    let extras = {};
    if (role === USER_ROLE.CONSUMER) {
      extras = await this.getConsumerCatalogs();
    }

    const promises = catalogs.map((catalog) => {
      return firstValueFrom(this.httpClient.get(`${this.apiUrl}/datas/${catalog}`)
        .pipe(
          map((response: any) => {
            return response.data;
          })
        ));
    });

    const results = await Promise.all(promises);
    
    const finalCatalogs = catalogs.reduce((acc: any, catalog, index) => {
      acc[catalog] = results[index];
      return acc;
    }, {});

    return {
      ...finalCatalogs,
      ...extras,
    };
  }

  // load tags 
  async getTagsFormApi(search: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/datas/tags?search=${search}&paginate=disabled`)
        .pipe(
          map((response: any) => {
            return response.data.map((tag: any) => {
              return {
                id: tag.id,
                title: tag.title,
                label: tag.title,
                value: tag.id,
              };
            });
          })
        ));
  }

  // check if user is logged in
  async isLoggedIn() {
    const token = this.getToken();
    if (!token) return false;
    
    // Check if token is expired
    if (await this.isTokenExpired()) {
      console.log('Token is expired');
      return false;
    }
    
    return true;
  }

  // Decode JWT and check if expired
  async isTokenExpired(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return true;

    try {
      // if userData return code 401 token is expired
      const userData = await this.getUserData();
      if (!userData) return true;
       
      return false;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }

  // Decode JWT token
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Validate token by making a test request
  async validateToken(): Promise<boolean> {
    try {
      await this.getUserData();
      return true;
    } catch (error) {
      return false;
    }
  }

  getUserRole() {
    return this.getUser()?.role?.name || null;
  }

  // save file
  async saveFileApi(file: File, folder: string) {
    const formData = new FormData();
    const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/client');
    formData.append('file', file);
    formData.append('folder', folder);

    return firstValueFrom(
      this.httpClient.post(`${apiUrl}/files`, formData, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        )
    );
  }

  verifyIfUserProfileIsComplete() {
    const user: any = this.getUser();
    const role = user?.role?.name;

    const data = {
      ...user?.profile,
      ...user?.profile?.consumer_extra,
    }
    
    if (role && role === USER_ROLE.CONSUMER) {
      const requiredFields = CONSUMER_FORM.filter(field => field.required);
      const missingFields = requiredFields.filter(field => data[field.key] === null || data[field.key] === undefined || data[field.key] === '');

      if (missingFields.length > 0) {
        return false;
      }
    }
    return true;
  }

  // google calerndar integration
  async getGoogleCalendarAuthUrl() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/auth/google/redirect`, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response;
          })
        )
    );    
  }

  // LOAD CATALOGS
  async getConsumerCatalogs() {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/datas/consumer-extras`, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        )
    );
  }

// PROFILE UNPUBLISH ADVISOR
  profileUnpublished() {
    const apiUrl = this.apiUrl.replace('/api/v1/public', '/api/v1/client');
    return firstValueFrom(
      this.httpClient.patch(`${apiUrl}/profile/unpublish`, {}, { headers: this.getAuthHeaders() })
        .pipe(
          map((response: any) => {
            return response.data;
          })
        )
    );
  }
}
